import { useRecordContext } from "ra-core";
import { ChangeEvent, FC, useState } from "react";
import { FormControlLabel, FormGroup, Grid, Switch, TextField, Typography, Autocomplete } from "@mui/material";
import * as Yup from 'yup';
import { ClassificationRuleDto } from "~/gen/api/securefile";
import { RuleComposeContextProps, RuleComposeContextProvider, RuleDisplayRoot, useRuleComposeController } from "./RuleDisplayComponents";
import { useClassificationRuleAmbinetDataCotnext } from "./ClassificationRuleAmbientDataProvider";
import { SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { SimpleOption } from "../governance/search/AmbientFacilitiesContext";


const validationSchema =
  Yup.object().shape({
    displayName: Yup.string().max(200, 'Rule Name must be at most 200 characters').required('Rule Name is required'),
    // classification: Yup.object().required('Target Classification is required'),
    priority: Yup.string().required('Priority is required'),
    comment: Yup.string().max(200, 'Comment must be at most 200 characters'),    
  })

interface FormData {
  displayName: string;
  classification: SecondaryClassificationResponseDto | null | "";
  priority: number;
  comment: string;
  active: boolean;
}

interface ClassificationRuleEditProps {
  getEffectClssItem: () => SecondaryClassificationResponseDto | "";
  setClssItem: (c: SecondaryClassificationResponseDto | "") => void;
  setActive: (a: boolean) => void;
  fieldProps: (path: keyof FormData, transform?: (x: any) => any) => {
    value: string | number | boolean | SecondaryClassificationResponseDto | null;
    onChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    error: boolean;
    helperText: string | undefined;
  };
  validateForm: () => Promise<boolean>;
  states: {
    prevValue: FormData,
    stage: FormData,
    errors: { [K in keyof FormData]+?: string | undefined; };
    touched: { [K in keyof FormData]+?: boolean | undefined; };
  };
  ruleComposeCtrls: RuleComposeContextProps;
  dirty: boolean;
  formDataValid: boolean;
}

export const ClassificationRuleEdit: FC<ClassificationRuleEditProps> = ({getEffectClssItem, setClssItem, setActive, fieldProps, states, ruleComposeCtrls }) => {
  const { classifications } = useClassificationRuleAmbinetDataCotnext()
  
  return (
    <>
      <Grid container sx={{ py: 2, }} spacing={2} >
        <Grid item xs={7} container spacing={2} >
          <Grid item xs={5}>
            <TextField label="Rule Name" fullWidth 
              {...fieldProps('displayName')}
            />
          </Grid>
          <Grid item xs={7}>
            <Autocomplete disableClearable={true}
              options={classifications}
              getOptionLabel={(option) => (option.text || option) as string}
              renderInput={(params) => 
                <TextField label="Target Classification" {...params} fullWidth 
                  error={Boolean(states.touched.classification && states.errors.classification)}
                  helperText={states.touched.classification ? states.errors.classification : undefined}
                />
              } 
              value={getEffectClssItem() as SimpleOption}
              onChange={(event, value, reason) => {
                if (reason === 'selectOption') {
                  if ((value as any)?.value === null) { // choosed '[None]'
                    setClssItem('')
                  } else {
                    const cls1 = value as SecondaryClassificationResponseDto
                    setClssItem(cls1)
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField label="Priority" sx={{ width: 150, mr: 12 }} type="number"
              {...fieldProps('priority', (x: any) => {
                let n = +x
                if (Number.isNaN(n)) { return 1 }
                n = !Number.isInteger ? Math.round(n) : n
                return n < 1 ? 1 : n
              })} />
          </Grid>
          <Grid item xs={7}>
            <FormGroup>
              <FormControlLabel 
                control={<Switch color="success" checked={states.stage.active} 
                  onChange={(e) => setActive(e.target.checked)} />}
                label="Active"
              />
            </FormGroup>
          </Grid>
        </Grid>
        <Grid item xs={5}>
          <TextField label="Comment" multiline fullWidth 
            {...fieldProps('comment')}
          />
        </Grid>
      </Grid>
      <Typography variant="subtitle2">Rule Definition</Typography>
      <RuleComposeContextProvider values={ruleComposeCtrls}>
        <RuleDisplayRoot />
      </RuleComposeContextProvider>
    </>
  )
}

export const useClassificationRuleEditController = (): ClassificationRuleEditProps => {
  const record = useRecordContext()
  const dto: ClassificationRuleDto|undefined = record as any
  const { classifications } = useClassificationRuleAmbinetDataCotnext()
  const ruleComposeCtrls = useRuleComposeController()

  const [states, setStates] = useState(() => {
    const initialValues = {
      displayName: dto?.displayName ?? '',
      classification: 
        (classifications.find(c => c.secondaryClassificationId === dto?.targetClassification) ?? null 
          ) as SecondaryClassificationResponseDto|null|'',
      // targetClassification: dto.targetClassification ?? '',
      priority: dto?.priority ?? 1,
      comment: dto?.comment ?? '',
      active: dto?.activeRule ?? true,
    }
    return ({
      prevValue: initialValues,
      stage: {...initialValues},
      errors: {} as { [K in keyof(typeof initialValues)]+?: string|undefined },
      touched: {} as { [K in keyof(typeof initialValues)]+?: boolean|undefined },
    })
  })

  const getEffectClssItem = () : SecondaryClassificationResponseDto|'' => {
    return states.stage.classification ?? ''
  }
  const setClssItem = (c: SecondaryClassificationResponseDto|'') => {
    setStates(v => {
      v.stage.classification = c
      if (v.prevValue.classification !== c) {
        v.touched.classification = true
      } else {
        delete v.touched.classification
      }
      v.errors = validate(v.stage) ?? {}
      // if (!c) {
      //   v.errors.classification = 'Target Classification is required'
      // } else {
      //   delete v.errors.classification
      // }
      return {...v}
    })
  }
  const setActive = (a: boolean) => {
    setStates(v => {
      v.stage.active = a
      if (v.prevValue.active !== v.stage.active) {
        v.touched.active = true
      } else {
        delete v.touched.active
      }
      return {...v}
    })
  }
  const validate = (x: any) => {
    try {
      validationSchema.validateSync(x, {abortEarly:false})
    } catch (e: any) {
      if (e.name === 'ValidationError') {
        const errors = {} as Record<string, string>
        for (let err of e.inner) {
          errors[err.path] = err.message
        }
        return errors
      }
    }
  }
  const validateForm = () => {
    return new Promise<boolean>(resolve => {
      setStates(v => {
        v.errors = validate(v.stage) ?? {}
        if (!v.stage.classification) { v.errors['classification'] = 'Target Classification is required' }
        v.touched = Object.keys(v.prevValue).reduce((acc, k) => {
          acc[k] = true
          return acc
        }, {} as any)
        resolve(Object.values(v.errors).every(x => !x))
        return {...v}
      })
    })
  }
  const fieldProps = (path: keyof typeof states.stage, transform?: (x:any)=>any) => ({
    value: states.stage[path],
    onChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setStates(v => {
        const value = e.target.value;
        (v.stage as any)[path] = transform ? transform(value) : value
        if (v.prevValue[path] !== value) {
          v.touched[path] = true
        } else {
          delete v.touched[path]
        }
        v.errors = validate(v.stage) ?? {}
        return {...v}
      })
    },
    error: Boolean(states.touched[path] && states.errors[path]),
    helperText: states.touched[path] ? states.errors[path] : undefined,
  })

  return {
    getEffectClssItem,
    setClssItem,
    setActive,
    validateForm,
    fieldProps,
    states,
    ruleComposeCtrls,
    dirty: Object.values(states.touched).some(x => x) || ruleComposeCtrls.states.isDirty,
    formDataValid: Object.values(states.errors).every(x => !x),
  }
}
